<template>
  <div class="floating-ticket-button">
    <!-- Sidebar assistant FAB — opens the right-drawer surface. -->
    <Button
      v-if="canAccessAssistant"
      v-tooltip.left="t('Assistente (novo)')"
      class="floating-btn assistant-fab"
      rounded
      data-testid="open-assistant-sidebar-fab"
      :aria-label="t('Assistente (novo)')"
      @click="openAssistantSidebar"
    >
      <i class="pi pi-sparkles speeddial-action-icon"></i>
    </Button>

    <!-- Support SpeedDial — Novo Chamado + Meus Chamados only (no assistant item). -->
    <SpeedDial
      :model="menuItems"
      direction="up"
      :style="{ left: 'calc(50% - 2rem)', bottom: 0 }"
      showIcon="pi pi-question-circle"
      hideIcon="pi pi-times"
      buttonClass="p-button-lg floating-btn"
      :tooltipOptions="{ position: 'left' } as any"
      :pt="{ pcButton: { root: { 'data-testid': 'floating-ticket-fab' } } }"
    >
      <template #item="{ item }">
        <Button
          v-tooltip.left="item.label"
          class="speeddial-action-btn"
          rounded
          :data-testid="`floating-ticket-${item.icon?.replace('pi pi-', '') ?? 'action'}`"
          @click="(event) => item.command?.({ originalEvent: event, item })"
        >
          <i :class="item.icon" class="speeddial-action-icon"></i>
        </Button>
      </template>
    </SpeedDial>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';
import { permissionsState } from '../services/permissionsService';
import { config } from '../services/configService';

const { t } = useI18n();
const router = useRouter();



// Decisão do usuário (2026-08-07, no merge do master): o assistente é liberado
// para TODOS os grupos. A allowlist anterior (Full/Franqueado/Gerente) existia
// porque só esses conseguiam ser debitados — mas o gate de cobrança vive no
// backend (billingFranquiaId obrigatório + fail-closed no reserve, skill
// billing-credits), não numa lista de grupos no front. Esconder o botão de
// quem tem sessão só escondia a funcionalidade; quem não pode pagar já é
// recusado na porta certa, com erro nomeado.
// Feature `assistente` (backend registry, skill feature-flags): a tenant without
// the AI assistant gets no FAB — and no drawer mounted (ModalManager).
const assistenteEnabled = config.isFeatureEnabled('assistente');
const canAccessAssistant = computed<boolean>(
  () => assistenteEnabled && (permissionsState.user?.groups?.length ?? 0) > 0,
);

interface MenuItem {
  label: string;
  icon: string;
  command: () => void;
}

/**
 * Os dois atalhos de suporte apontam para os CHAMADOS DA PLATAFORMA
 * (`/chamados`), não mais para a tela do Odoo (`/suporte/chamados`).
 *
 * Decisão do usuário (2026-09-02): trazer os chamados para dentro. A tela do
 * Odoo continua existindo e alcançável por URL — ela guarda o histórico que
 * NÃO será migrado —, mas deixa de ser o caminho que a interface oferece.
 *
 * Repare que a tela do Odoo já tinha `showInMenu: false` no router: ela nunca
 * esteve no menu lateral, e ESTE botão flutuante era o único caminho até ela.
 * Trocar aqui é, na prática, trocar a porta de entrada do suporte inteiro.
 */
const menuItems = computed((): MenuItem[] => [
  {
    label: t('Novo Chamado'),
    icon: 'pi pi-plus',
    command: () => {
      log('FloatingTicketButton', 'openTicketModal', 'Abrindo o modal de chamado da plataforma');
      eventBus.emit('modal.chamados.open', { modo: 'novo' });
    },
  },
  {
    label: t('Meus Chamados'),
    icon: 'pi pi-list',
    command: () => {
      log('FloatingTicketButton', 'viewTickets', 'Indo para a lista de chamados da plataforma');
      router.push('/chamados');
    },
  },
]);

function openAssistantSidebar() {
  log('FloatingTicketButton', 'openAssistantSidebar', 'Opening assistant sidebar');
  eventBus.emit('sidebar.assistant.open');
}
</script>

<style scoped>

.floating-ticket-button {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 12px;
}

.assistant-fab {
  background: var(--p-primary-color) !important;
  color: var(--p-primary-contrast-color) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease !important;
}

.assistant-fab:hover {
  background: var(--p-primary-hover-color) !important;
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

:deep(.floating-btn) {
  width: 56px !important;
  height: 56px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

:deep(.floating-btn:hover) {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

:deep(.speeddial-action-btn) {
  width: 56px !important;
  height: 56px !important;
  background: var(--p-primary-color) !important;
  color: var(--p-primary-contrast-color) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease !important;
}

:deep(.speeddial-action-btn:hover) {
  background: var(--p-primary-hover-color) !important;
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3) !important;
}

:deep(.speeddial-action-icon) {
  font-size: 1.5rem !important;
  color: var(--p-primary-contrast-color) !important;
}

/* Responsive positioning for smaller screens */
@media (max-width: 768px) {
  .floating-ticket-button {
    bottom: 16px;
    right: 16px;
  }

  :deep(.floating-btn) {
    width: 48px !important;
    height: 48px !important;
  }

  :deep(.speeddial-action-btn) {
    width: 48px !important;
    height: 48px !important;
  }

  :deep(.speeddial-action-icon) {
    font-size: 1.3rem !important;
  }
}
</style>
